// ════════════════════════════════════════════════════════════════════════════
// ✅ App.js — FIXED: Auto-dismiss on resolve + Nearby responder list
// ════════════════════════════════════════════════════════════════════════════

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react";
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import Dashboard from "./pages/Dashboard";
import Bookings from "./pages/Bookings";
// import Drivers from "./pages/Drivers";
import Users from "./pages/Users";
import Earnings from "./pages/Earnings";
import Settings from "./pages/Settings";
import Login from "./pages/Login";
import Register from "./pages/Register";
import "./App.css";
import { ToastContainer } from "react-toastify";
import DriversMap from "./pages/DriversMap";
import Organizations from "./pages/Organizations";
import FareConfigure from "./pages/FareConfigure";
import { socket } from "./socket";
import sosSound from "./assets/sounds/sos.mp3";
import Emergencies from "./pages/Emergencies";
import EmergencyControl from "./pages/EmergencyControl";
import SubscriptionsPage from "./pages/SubscriptionsPage";
import Payments from "./pages/Payments";
import PassengerWalletPage from "./pages/PassengerWalletPage";
import PendingCash from "./pages/PendingCash";
import DriverHistory from "./pages/Driverhistory";
import DriverHistoryList from "./pages/DriverHistoryList";
import SuggestionsPage from "./pages/SuggestionsPage";
import StaffPage from "./pages/StaffPage";
import PublicEmergencies from "./pages/PublicEmergencies";
import PassengersMap from "./pages/PassengersMap";
import RideAnalytics from "./pages/RideAnalytics";
import AdvertisementPage from "./pages/AdvertisementPage";
import NotificationPage from "./pages/Notificationpage";
import ComplaintsPage from "./pages/ComplaintsPage";
import OfferPage from "./pages/OfferPage";
import ZonesPage from "./pages/ZonesPage";
import NotFoundPage from "./pages/NotfoundPage";
import IssuePage from "./pages/IssuePage";
import BankDeposits from "./pages/BankDeposits";
import AttendancePage from "./pages/AttendancePage";
import TerritoryReport from "./pages/TerritoryReport";
import SalaryPage from "./pages/SalaryPage";
import ZonePlanPage from "./pages/ZonePlanPage";
import PlansPage from "./pages/PlanPage";
import AgentActivatePage from "./pages/AgentActivatePage";
import ProfilePage from "./pages/ProfilePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import LiveRideTracker from "./pages/LiveRideTracker";
import BdePerformancePage from "./pages/BdePerformancePage";
import BdeDashboardPage from "./pages/BdeDashboardPage";
import JobApplicationsPage from "./pages/JobApplicationsPage";
import DriverReferralPayoutsPage from "./pages/DriverReferralPayoutsPage";
import DriversPage from "./pages/DriversPage";
import SidebarConfigPage from "./pages/SidebarConfigPage";
import AutoLogout from "./components/AutoLogout";
import TwoFactorAuth from "./pages/TwoFactorAuth";
import DriverSettlementsPage from "./pages/DriverSettlementsPage";

const REASON_EMOJI = {
  Accident: "💥",
  "Driver misbehaviour": "⚠️",
  "Medical emergency": "🏥",
  "Robbery / Theft": "🦹",
  "Vehicle breakdown": "🛺",
  "Unsafe driving": "🚦",
  Harassment: "🚫",
  "Natural disaster": "🌊",
  "Fire emergency": "🔥",
  Other: "❗",
};

// ✅ Admin socket registration
socket.on("connect", () => {
  const admin = localStorage.getItem("admin");
  const adminData = admin ? JSON.parse(admin) : null;
  const adminId = adminData?._id || adminData?.roleId || "admin_1";
  const token = localStorage.getItem("token");
  if (token) {
    socket.emit("admin-online", { adminId });
    console.log("✅ Admin socket connected:", socket.id, "adminId:", adminId);
  }
});

const token = localStorage.getItem("token");
if (token && !socket.connected) {
  socket.connect();
}

socket.on("disconnect", () => {
  console.log("❌ Admin socket disconnected");
});

// Audio unlock
let audioUnlocked = false;
const unlockAudio = () => {
  if (audioUnlocked) return;
  const silent = new Audio(sosSound);
  silent.volume = 0;
  silent
    .play()
    .then(() => {
      silent.pause();
      silent.currentTime = 0;
      audioUnlocked = true;
    })
    .catch(() => {});
};
document.addEventListener("click", unlockAudio, { once: true });
document.addEventListener("keydown", unlockAudio, { once: true });
document.addEventListener("touchstart", unlockAudio, { once: true });

function PrivateRoute({ children }) {
  const token = useSelector((state) => state.auth.token);
  return token ? children : <Navigate to="/login" />;
}

// ✅ AGENT role check — read logged-in role from localStorage("admin")
// AGENT login-ஆனா AgentActivatePage மட்டும் தான் open ஆகணும்.
function getStoredRole() {
  try {
    const admin = localStorage.getItem("admin");
    const parsed = admin ? JSON.parse(admin) : null;
    return parsed?.role || null;
  } catch {
    return null;
  }
}

// ════════════════════════════════════════════════════════════════════════════
// ✅ SOS OVERLAY — FIXED: Auto-dismiss + Responder list
// ════════════════════════════════════════════════════════════════════════════
function SOSOverlay() {
  const [sosAlerts, setSosAlerts] = useState([]);
  const alarmRef = useRef(null);

  const getAddress = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
      );
      const data = await res.json();
      if (data.status === "OK" && data.results?.length > 0) {
        const components = data.results[0].address_components;
        const sub = components.find(
          (c) =>
            c.types.includes("sublocality") ||
            c.types.includes("sublocality_level_1"),
        )?.long_name;
        const loc = components.find((c) =>
          c.types.includes("locality"),
        )?.long_name;
        if (sub && loc) return `${sub}, ${loc}`;
        if (loc) return loc;
        if (sub) return sub;
        return data.results[0].formatted_address
          .split(",")
          .slice(0, 2)
          .join(",");
      }
      return `${lat?.toFixed(4)}, ${lng?.toFixed(4)}`;
    } catch (e) {
      return `${lat?.toFixed(4)}, ${lng?.toFixed(4)}`;
    }
  };

  const playAlarm = () => {
    try {
      const alarm = new Audio("/assets/sounds/sos.mp3");
      alarm.volume = 1.0;
      alarm.loop = true;
      alarm.play().catch((e) => console.log("Audio blocked:", e));
      alarmRef.current = alarm;
    } catch (e) {
      console.log("Audio error:", e);
    }
  };

  // ✅ HELPER — Stop alarm if no active alerts remain
  const stopAlarmIfEmpty = (alertsAfterUpdate) => {
    if (alertsAfterUpdate.length === 0 && alarmRef.current) {
      alarmRef.current.pause();
      alarmRef.current.currentTime = 0;
      alarmRef.current = null;
    }
  };

  useEffect(() => {
    // ✅ RIDE-BASED SOS (existing)
    socket.on("sos-alert", async (data) => {
      console.log("🚨 RIDE SOS received:", data);
      const address = await getAddress(data.location?.lat, data.location?.lng);
      playAlarm();
      setSosAlerts((prev) => [
        {
          ...data,
          address,
          id: Date.now(),
          type: "ride",
          responders: [], // ✅ Initialize responders array
        },
        ...prev,
      ]);
    });

    // ✅ PUBLIC SOS (from HomeScreen)
    socket.on("public-sos-alert", async (data) => {
      console.log("🚨 PUBLIC SOS received:", data);
      const address = await getAddress(data.location?.lat, data.location?.lng);
      playAlarm();
      setSosAlerts((prev) => [
        {
          ...data,
          address,
          id: Date.now(),
          type: "public",
          responders: [], // ✅ Initialize responders array
        },
        ...prev,
      ]);
    });

    // ════════════════════════════════════════════════════════════════
    // ✅ FIX 1: PUBLIC SOS CANCELLED — Auto-dismiss alert
    // ════════════════════════════════════════════════════════════════
    socket.on("public-sos-cancelled", (data) => {
      console.log("ℹ️ Public SOS cancelled:", data.sosId);
      setSosAlerts((prev) => {
        const updated = prev.map((a) =>
          a.sosId === data.sosId
            ? {
                ...a,
                resolved: true,
                resolvedType: "cancelled",
                resolvedMessage: "Sender cancelled — Problem resolved",
              }
            : a,
        );
        // ✅ Auto-dismiss after 5 seconds
        setTimeout(() => {
          setSosAlerts((p) => {
            const filtered = p.filter((a) => a.sosId !== data.sosId);
            stopAlarmIfEmpty(filtered);
            return filtered;
          });
        }, 5000);
        return updated;
      });
      // ✅ Stop alarm immediately when cancelled
      if (alarmRef.current) {
        alarmRef.current.pause();
        alarmRef.current.currentTime = 0;
        alarmRef.current = null;
      }
    });

    // ════════════════════════════════════════════════════════════════
    // ✅ FIX 2: SOS RESOLVED (by responder/admin) — Auto-dismiss
    // ════════════════════════════════════════════════════════════════
    socket.on("sos-status-update", (data) => {
      console.log("📡 SOS status update:", data);
      setSosAlerts((prev) => {
        const updated = prev.map((a) =>
          a.sosId === data.sosId
            ? {
                ...a,
                resolved: true,
                resolvedType: data.isReal ? "real" : "fake",
                resolvedMessage: data.isReal
                  ? "✅ Real emergency — Help arrived"
                  : "🚫 Marked as false alarm",
                resolvedBy: data.resolvedByName || "Someone",
              }
            : a,
        );
        // ✅ Auto-dismiss after 8 seconds
        setTimeout(() => {
          setSosAlerts((p) => {
            const filtered = p.filter((a) => a.sosId !== data.sosId);
            stopAlarmIfEmpty(filtered);
            return filtered;
          });
        }, 8000);
        return updated;
      });
      // ✅ Stop alarm immediately when resolved
      if (alarmRef.current) {
        alarmRef.current.pause();
        alarmRef.current.currentTime = 0;
        alarmRef.current = null;
      }
    });

    // ════════════════════════════════════════════════════════════════
    // ✅ FIX 3: SOS RESPONSE RECEIVED — Add to responders list
    // ════════════════════════════════════════════════════════════════
    socket.on("sos-response-received", (data) => {
      console.log("🆘 SOS Response:", data);
      setSosAlerts((prev) =>
        prev.map((a) =>
          a.sosId === data.sosId
            ? {
                ...a,
                hasResponder: true,
                // ✅ Add to responders array (don't overwrite previous)
                responders: [
                  ...(a.responders || []),
                  {
                    name: data.responderName || "Someone",
                    phone: data.responderPhone || "",
                    type: data.responseType || "coming_to_help",
                    time: new Date().toLocaleTimeString(),
                  },
                ],
              }
            : a,
        ),
      );
    });

    // ════════════════════════════════════════════════════════════════
    // ✅ FIX 4: PASSENGER RESOLVED SOS — Responder marked real/fake
    // ════════════════════════════════════════════════════════════════
    socket.on("passenger-resolved-sos-admin", (data) => {
      console.log("📡 Passenger resolved SOS:", data);
      setSosAlerts((prev) => {
        const updated = prev.map((a) =>
          a.sosId === data.sosId
            ? {
                ...a,
                resolved: true,
                resolvedType: data.isReal ? "real" : "fake",
                resolvedMessage: data.isReal
                  ? `✅ Real emergency — Resolved by ${data.resolvedByName || "nearby passenger"}`
                  : `🚫 False alarm — Verified by ${data.resolvedByName || "nearby passenger"}`,
                resolvedBy: data.resolvedByName || "Passenger",
              }
            : a,
        );
        setTimeout(() => {
          setSosAlerts((p) => {
            const filtered = p.filter((a) => a.sosId !== data.sosId);
            stopAlarmIfEmpty(filtered);
            return filtered;
          });
        }, 8000);
        return updated;
      });
      if (alarmRef.current) {
        alarmRef.current.pause();
        alarmRef.current.currentTime = 0;
        alarmRef.current = null;
      }
    });

    return () => {
      socket.off("sos-alert");
      socket.off("public-sos-alert");
      socket.off("public-sos-cancelled");
      socket.off("sos-response-received");
      socket.off("sos-status-update");
      socket.off("passenger-resolved-sos-admin");
    };
  }, []);

  const dismiss = (id) => {
    setSosAlerts((prev) => {
      const filtered = prev.filter((a) => a.id !== id);
      stopAlarmIfEmpty(filtered);
      return filtered;
    });
    if (alarmRef.current && sosAlerts.length <= 1) {
      alarmRef.current.pause();
      alarmRef.current.currentTime = 0;
      alarmRef.current = null;
    }
  };

  // ✅ Response type label helper
  const getResponseLabel = (type) => {
    switch (type) {
      case "coming_to_help":
        return "🏃 Coming to help";
      case "called_emergency":
        return "📞 Called 112";
      case "notified_others":
        return "📢 Notified others";
      default:
        return "🆘 Responded";
    }
  };

  // Admin React Component
  const SOSVoicePlayer = ({ voiceUrl }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);

    if (!voiceUrl) return null;

    const toggle = () => {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        audioRef.current.play();
        setPlaying(true);
      }
    };

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: playing ? "#ECFDF5" : "#EFF6FF",
          border: `1.5px solid ${playing ? "#6EE7B7" : "#BFDBFE"}`,
          borderRadius: 12,
          padding: "10px 16px",
          width: "fit-content",
        }}
      >
        <audio
          ref={audioRef}
          src={voiceUrl}
          onEnded={() => setPlaying(false)}
          style={{ display: "none" }}
        />
        <button
          onClick={toggle}
          style={{
            background: playing ? "#10B981" : "#2563EB",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "8px 16px",
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {playing ? "⏸ Pause" : "▶ Play Voice"}
        </button>
        <span style={{ fontSize: 12, color: "#6B7280" }}>
          🎙 SOS Voice Message
        </span>
      </div>
    );
  };

  if (sosAlerts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      {sosAlerts.map((alert) => (
        <div
          key={alert.id}
          style={{
            backgroundColor: alert.resolved
              ? alert.resolvedType === "real"
                ? "#059669"
                : alert.resolvedType === "fake"
                  ? "#D97706"
                  : "#6B7280"
              : alert.type === "public"
                ? "#7C3AED"
                : "#e74c3c",
            color: "#fff",
            borderRadius: 14,
            padding: "18px 22px",
            minWidth: 340,
            maxWidth: 420,
            boxShadow: alert.resolved
              ? "0 4px 24px rgba(0,0,0,0.3)"
              : alert.type === "public"
                ? "0 4px 24px rgba(124,58,237,0.6)"
                : "0 4px 24px rgba(231,76,60,0.6)",
            animation: alert.resolved
              ? "none"
              : "sosPulse 1.2s ease-in-out infinite",
            transition: "all 0.3s ease",
          }}
        >
          {/* ════════════════════════════════════════════════════════
              ✅ RESOLVED BANNER — Shows when SOS is resolved
          ════════════════════════════════════════════════════════ */}
          {alert.resolved && (
            <div
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 12,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>
                {alert.resolvedType === "real"
                  ? "✅"
                  : alert.resolvedType === "fake"
                    ? "🚫"
                    : "ℹ️"}
              </div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>
                {alert.resolvedType === "cancelled"
                  ? "Problem Resolved"
                  : alert.resolvedType === "real"
                    ? "Real Emergency — Resolved"
                    : "False Alarm"}
              </div>
              <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
                {alert.resolvedMessage}
              </div>
              {alert.resolvedBy && (
                <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>
                  By: {alert.resolvedBy}
                </div>
              )}
              <div
                style={{
                  fontSize: 10,
                  opacity: 0.7,
                  marginTop: 6,
                  fontStyle: "italic",
                }}
              >
                Auto-dismissing...
              </div>
            </div>
          )}

          {/* Header */}
          <div
            style={{
              fontSize: 16,
              fontWeight: 900,
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {alert.type === "public" ? (
              <>
                <span
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    padding: "2px 8px",
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                  }}
                >
                  PUBLIC
                </span>
                🚨 PUBLIC SOS ALERT
              </>
            ) : (
              <>🚨 RIDE SOS ALERT</>
            )}
          </div>

          {/* Passenger */}
          <div style={{ fontSize: 14, marginBottom: 4 }}>
            👤 <strong>{alert.passengerName}</strong>
          </div>
          <div style={{ fontSize: 14, marginBottom: 4 }}>
            📞 {alert.passengerMobile || "N/A"}
          </div>

          {/* Ride ID (only for ride SOS) */}
          {alert.type === "ride" && alert.rideId && (
            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>
              🎫 Ride: {alert.rideId}
            </div>
          )}

          {/* Source (only for public SOS) */}
          {alert.type === "public" && alert.source && (
            <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4 }}>
              📱 Source: {alert.source}
            </div>
          )}

          {/* Reason */}
          {alert.reason && (
            <div
              style={{
                fontSize: 13,
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: 8,
                padding: "6px 10px",
                marginBottom: 6,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {REASON_EMOJI[alert.reason] || "❗"} {alert.reason}
            </div>
          )}

          {/* Reason Note */}
          {alert.reasonNote && (
            <div
              style={{
                fontSize: 12,
                backgroundColor: "rgba(255,255,255,0.15)",
                borderRadius: 8,
                padding: "5px 10px",
                marginBottom: 6,
                fontStyle: "italic",
              }}
            >
              "{alert.reasonNote}"
            </div>
          )}

          {/* Voice Message player */}
          {alert.voiceMessage && (
            <div style={{ marginBottom: 6 }}>
              <SOSVoicePlayer voiceUrl={alert.voiceMessage} />
            </div>
          )}

          {/* ════════════════════════════════════════════════════════
              ✅ FIX: NEARBY RESPONDERS LIST
          ════════════════════════════════════════════════════════ */}
          {alert.responders && alert.responders.length > 0 && (
            <div
              style={{
                backgroundColor: "rgba(16,185,129,0.25)",
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 8,
                borderLeft: "3px solid #10B981",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  marginBottom: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                🆘 Nearby Responders ({alert.responders.length})
              </div>
              {alert.responders.map((r, idx) => (
                <div
                  key={idx}
                  style={{
                    fontSize: 12,
                    padding: "4px 0",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    borderTop:
                      idx > 0 ? "1px solid rgba(255,255,255,0.15)" : "none",
                    paddingTop: idx > 0 ? 6 : 4,
                    marginTop: idx > 0 ? 2 : 0,
                  }}
                >
                  <span style={{ flex: 1 }}>
                    <strong>{r.name}</strong>
                    {r.phone && (
                      <span style={{ opacity: 0.8, marginLeft: 6 }}>
                        📞 {r.phone}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      backgroundColor: "rgba(255,255,255,0.2)",
                      padding: "2px 8px",
                      borderRadius: 6,
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getResponseLabel(r.type)}
                  </span>
                  <span style={{ fontSize: 10, opacity: 0.7 }}>{r.time}</span>
                </div>
              ))}
            </div>
          )}

          {/* Location */}
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            📍{" "}
            {alert.address ||
              `${alert.location?.lat?.toFixed(5)}, ${alert.location?.lng?.toFixed(5)}`}
          </div>

          {/* Time */}
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 14 }}>
            🕐 {new Date(alert.time || alert.timestamp).toLocaleTimeString()}
          </div>

          {/* Priority badge for public SOS */}
          {alert.priority === "high" && (
            <div
              style={{
                fontSize: 11,
                backgroundColor: "rgba(255,255,255,0.2)",
                borderRadius: 6,
                padding: "3px 8px",
                marginBottom: 10,
                fontWeight: 700,
                display: "inline-block",
              }}
            >
              ⚡ HIGH PRIORITY
            </div>
          )}

          {/* Dismiss */}
          <button
            onClick={() => dismiss(alert.id)}
            style={{
              width: "100%",
              backgroundColor: alert.resolved
                ? "rgba(255,255,255,0.3)"
                : "#fff",
              color: alert.resolved
                ? "#fff"
                : alert.type === "public"
                  ? "#7C3AED"
                  : "#e74c3c",
              border: alert.resolved
                ? "1px solid rgba(255,255,255,0.4)"
                : "none",
              borderRadius: 8,
              padding: "8px 0",
              fontWeight: 800,
              fontSize: 14,
              cursor: "pointer",
              marginTop: 8,
            }}
          >
            {alert.resolved ? "✓ Close Now" : "✓ Dismiss"}
          </button>
        </div>
      ))}
    </div>
  );
}

export default function App() {
  const isAgent = getStoredRole() === "AGENT";

  return (
    <BrowserRouter>
      <SOSOverlay />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route
          path="/unauthorized"
          element={<NotFoundPage mode="unauthorized" />}
        />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <div className="app-layout">
                <Sidebar />
                <div className="main-area">
                  <Topbar />
                  <main className="page-content">
                    {isAgent ? (
                      // ✅ AGENT role — AgentActivatePage மட்டும் தான், வேற எந்த route-க்கும்
                      // போனாலும் /agent-activate க்கு redirect ஆகும்.
                      <Routes>
                        <Route
                          path="/agent-activate"
                          element={<AgentActivatePage />}
                        />
                        <Route
                          path="*"
                          element={<Navigate to="/agent-activate" replace />}
                        />
                      </Routes>
                    ) : (
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/bookings" element={<Bookings />} />
                        <Route path="/drivers" element={<DriversPage />} />
                        <Route path="/drivers-map" element={<DriversMap />} />
                        <Route path="/users" element={<Users />} />
                        <Route
                          path="/passengers-map"
                          element={<PassengersMap />}
                        />
                        <Route path="/earnings" element={<Earnings />} />
                        <Route
                          path="/ride-analytics"
                          element={<RideAnalytics />}
                        />
                        <Route path="/settings" element={<Settings />} />
                        <Route
                          path="/organizations"
                          element={<Organizations />}
                        />
                        <Route
                          path="/fare-configure"
                          element={<FareConfigure />}
                        />
                        <Route
                          path="/advertisements"
                          element={<AdvertisementPage />}
                        />
                        <Route path="/emergencies" element={<Emergencies />} />
                        <Route
                          path="/public-emergencies"
                          element={<PublicEmergencies />}
                        />
                        <Route
                          path="/emergency-control"
                          element={<EmergencyControl />}
                        />
                        <Route
                          path="/subscriptions"
                          element={<SubscriptionsPage />}
                        />
                        <Route path="/payments" element={<Payments />} />
                        <Route
                          path="/passenger-wallet"
                          element={<PassengerWalletPage />}
                        />
                        <Route path="/pending-cash" element={<PendingCash />} />
                        <Route
                          path="/bank-deposits"
                          element={<BankDeposits />}
                        />
                        <Route
                          path="/attendance"
                          element={<AttendancePage />}
                        />
                        <Route path="/salary" element={<SalaryPage />} />
                        <Route path="/plans" element={<PlansPage />} />
                        <Route path="/zone-plans" element={<ZonePlanPage />} />
                        <Route
                          path="/driver-history"
                          element={<DriverHistoryList />}
                        />
                        <Route
                          path="/drivers/:driverId/history"
                          element={<DriverHistory />}
                        />
                        <Route
                          path="/suggestions"
                          element={<SuggestionsPage />}
                        />
                        <Route
                          path="/notifications"
                          element={<NotificationPage />}
                        />
                        <Route
                          path="/complaints"
                          element={<ComplaintsPage />}
                        />
                        <Route path="/issues" element={<IssuePage />} />
                        <Route path="/staff" element={<StaffPage />} />
                        <Route path="/offers" element={<OfferPage />} />
                        <Route
                          path="/territory-report"
                          element={<TerritoryReport />}
                        />
                        <Route
                          path="/organizations/:orgId/zones"
                          element={<ZonesPage />}
                        />
                        <Route
                          path="/agent-activate"
                          element={<AgentActivatePage />}
                        />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route
                          path="/live-rides-tracker"
                          element={<LiveRideTracker />}
                        />
                        <Route
                          path="/change-password"
                          element={<ChangePasswordPage />}
                        />
                        <Route
                          path="/bde-performance"
                          element={<BdePerformancePage />}
                        />
                        <Route
                          path="/bde-dashboard"
                          element={<BdeDashboardPage />}
                        />
                        <Route
                          path="/job-applications"
                          element={<JobApplicationsPage />}
                        />
                        <Route
                          path="/driver-referral-payouts"
                          element={<DriverReferralPayoutsPage />}
                        />
                        <Route
                          path="/sidebar-config"
                          element={<SidebarConfigPage />}
                        />
                        <Route
                          path="/two-factor-auth"
                          element={<TwoFactorAuth />}
                        />
                        <Route path="*" element={<NotFoundPage />} />
                        <Route
                          path="/driver-settlements"
                          element={<DriverSettlementsPage />}
                        />
                      </Routes>
                    )}
                  </main>
                </div>
              </div>
            </PrivateRoute>
          }
        />
      </Routes>
      <AutoLogout />
      <ToastContainer position="top-right" autoClose={3000} />
    </BrowserRouter>
  );
}
